import React, { useEffect, useMemo, useState } from 'react';
import {
    Avatar,
    AvatarGroup,
    Autocomplete,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    List,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import _ from "lodash";
import { getAvatarInitials } from "app/utils/appHelpers";
import Div from "@jumbo/shared/Div";
import userServices from "app/services/user-services";
import CustomPagination from 'app/widgets/Shared/Pagination/CustomPagination';
import { HOST } from 'app/utils/constants/paths';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import UserEditModal from './UserEditModal';
import { CreateButton, StandardFiltersBar, StandardPageHeader, StandardSearchField } from 'app/shared/ui';
import useSwalWrapper from "@jumbo/vendors/sweetalert2/hooks";
import { defaultSwalPreferences, hasAnyProfile } from "app/utils/appHelpers";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { LoadingButton } from '@mui/lab';

const EMPTY_INVITE_FORM = {
    email: '',
    first_name: '',
    last_name: '',
    profile_names: [],
};

const UsersIndex = () => {
    const Swal = useSwalWrapper();
    const { authUser } = useJumboAuth();
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [usersList, setUsersList] = useState([])
    const [searchQuery, setSearchQuery] = useState('')
    const [listLoading, setListLoading] = useState(false)
    const [currentUserId, setCurrentUserId] = useState(null)
    const [openUserEditModal, setOpenUserEditModal] = useState(false)
    const [openInviteModal, setOpenInviteModal] = useState(false)
    const [inviteForm, setInviteForm] = useState(EMPTY_INVITE_FORM)
    const [inviteSubmitting, setInviteSubmitting] = useState(false)
    const [inviteOptionsLoading, setInviteOptionsLoading] = useState(false)
    const [inviteProfileOptions, setInviteProfileOptions] = useState([])

    const canInviteUsers = hasAnyProfile(authUser, ['customer_admin', 'superadmin']);

    const defaultInviteProfiles = (options) => {
        if (!options?.length) return [];
        const reader = _.find(options, { name: 'reader' });
        return reader ? ['reader'] : [options[0].name];
    };

    useEffect(() => {
        if(listLoading){
            loadUsers()
        }
    }, [listLoading])

    useEffect(() => {
        setListLoading(true)
    }, [])

    useEffect(() => {
        if (!openInviteModal) return;
        if (inviteForm.profile_names.length > 0) return;

        const defaults = defaultInviteProfiles(inviteProfileOptions);
        if (!defaults.length) return;

        setInviteForm((prev) => ({ ...prev, profile_names: defaults }));
    }, [openInviteModal, inviteProfileOptions]);

    const buildUsersParams = () => {
        let par = []
        let pos = 0
        par.push(encodeURI(`q[m]=and`))
        if(searchQuery && searchQuery !== 0) {
            par.push(encodeURI(`q[g][${pos}][first_name_or_last_name_or_email_or_code_cont]=${searchQuery}`))
            pos += 1
        }

        par.push(`page=${currentPage}`)
        par.push(`include=companies`)

        par = _.join(par, '&')

        return `?${par}`;
    }

    const loadUsers = () => {
        userServices.getUsers(buildUsersParams())
            .then(resp => {
                setUsersList(resp.data)
                setTotalPages(resp.meta.total_pages)
            })
            .catch(function (error) {})
            .finally(() => {
                setListLoading(false)
            });
    }

    const loadInviteOptions = () => {
        if (inviteOptionsLoading || inviteProfileOptions.length > 0) return;

        setInviteOptionsLoading(true);
        userServices.getEditOptions()
            .then((resp) => {
                setInviteProfileOptions(resp?.profile_names || []);
            })
            .finally(() => {
                setInviteOptionsLoading(false);
            });
    };

    const openInviteDialog = () => {
        loadInviteOptions();
        setOpenInviteModal(true);
    };

    const closeInviteDialog = () => {
        setOpenInviteModal(false);
        setInviteSubmitting(false);
        setInviteForm({
            ...EMPTY_INVITE_FORM,
            profile_names: defaultInviteProfiles(inviteProfileOptions),
        });
    };

    const submitInvitation = () => {
        if (!inviteForm.email?.trim()) return;

        setInviteSubmitting(true);
        userServices.inviteUser({
            user: {
                email: inviteForm.email.trim(),
                first_name: inviteForm.first_name?.trim() || null,
                last_name: inviteForm.last_name?.trim() || null,
                profile_names: inviteForm.profile_names,
            },
        })
            .then(() => {
                Swal.mixin(defaultSwalPreferences(Swal)).fire({
                    icon: 'success',
                    title: 'Invitación enviada',
                    background: 'success',
                });
                closeInviteDialog();
                setCurrentPage(1);
                setListLoading(true);
            })
            .finally(() => {
                setInviteSubmitting(false);
            });
    };

    const selectedInviteProfiles = useMemo(
        () => _.filter(inviteProfileOptions, (option) => _.includes(inviteForm.profile_names, option.name)),
        [inviteForm.profile_names, inviteProfileOptions]
    );

    const handleSearchQueryChange = (newVal) => {
        setCurrentPage(1)
        setSearchQuery(newVal)
        setListLoading(true)
    }

    const handleUserChange = (newUser) => {
        setUsersList(_.map(usersList, (u) => { return u.id == newUser.id ? newUser : u }))
    }

    const debounceSearchQueryChange = _.debounce(handleSearchQueryChange, 450)

    const renderUsersList = () => {
        if(usersList){
            return (
                <React.Fragment>
                    <UserEditModal userID={currentUserId} isOpen={openUserEditModal} onClose={(changed, userOutput) => { setOpenUserEditModal(false) }} onUserChange={handleUserChange} />
                    <List disablePadding>
                        {_.map(usersList, (u, i) => {
                            return (
                                <React.Fragment key={u.id}>
                                    <Card sx={{mb: 2}}>
                                        <CardContent sx={{display: 'flex'}}>
                                            <Grid container spacing={5}>
                                                <Grid item xs={12} md={4}>
                                                    <Stack
                                                        direction="column"
                                                        justifyContent="start"
                                                        alignItems="left"
                                                        spacing={0.5}>
                                                        <Typography variant={"overline"} color="primary">{u.email}</Typography>
                                                        <Typography variant={"button"} color="primary">{u.first_name} {u.last_name}</Typography>
                                                    </Stack>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Stack
                                                        direction="column"
                                                        justifyContent="start"
                                                        alignItems="left"
                                                        spacing={0.5}>

                                                        <Typography variant={"overline"}>Empresas asociadas</Typography>
                                                        { u.companies?.length <= 0 && <Typography variant={"body"}>-</Typography> }
                                                        <AvatarGroup max={3} sx={{justifyContent: 'flex-end', cursor: 'pointer', '.MuiAvatarGroup-avatar': { backgroundColor: '#EEEEEE' }}}>
                                                            { _.map(_.uniqBy(u.companies, 'id'), (c) => {
                                                                return (
                                                                    <Tooltip title={c.name} key={`${u.id}_${c.id}`}>
                                                                        { c.logo_url ?
                                                                            <Avatar src={`${HOST}${c.logo_url}`} />
                                                                            :
                                                                            <Avatar alt={c.name}>
                                                                                {getAvatarInitials(c.name)}
                                                                            </Avatar>
                                                                        }
                                                                    </Tooltip>
                                                                )
                                                            })}
                                                        </AvatarGroup>
                                                    </Stack>
                                                </Grid>
                                                <Grid item xs={12} md={3} style={{height: '100%'}}>
                                                </Grid>
                                                <Grid item xs={12} md={1} style={{height: '100%'}}>
                                                    <IconButton onClick={() => { setCurrentUserId(u.id); setOpenUserEditModal(true) }}>
                                                        <EditOutlinedIcon />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </React.Fragment>
                            )
                        })}
                    </List>
                </React.Fragment>
            )
        } else {
            return(
                <Typography>
                    No se encontraron datos
                </Typography>
            )
        }
    }
    
    return (
        <div>
            <StandardPageHeader title="Usuarios" />

            <StandardFiltersBar>
                <Grid container spacing={1.25} alignItems="center">
                    <Grid item xs={12} md={8}>
                        <StandardSearchField
                            fullWidth
                            placeholder="Buscar"
                            onChange={(e) => debounceSearchQueryChange(e.target.value)}
                            onEnter={(value) => handleSearchQueryChange(value)}
                        />
                    </Grid>
                    {canInviteUsers && (
                        <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                            <CreateButton
                                fullWidth={false}
                                startIcon={<AddCircleOutlineOutlinedIcon />}
                                onClick={openInviteDialog}
                            >
                                Invitar usuario
                            </CreateButton>
                        </Grid>
                    )}
                </Grid>
            </StandardFiltersBar>

            <Div sx={{display: listLoading ? 'block' : 'none', textAlign: 'center', my: 5}}>
                <CircularProgress/>
            </Div>
            <Grid container spacing={3.75}>
                <Grid item xs={12}>
                    {renderUsersList()}
                </Grid>
            </Grid>
            <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageSelect={(page) => { setCurrentPage(page); setListLoading(true)}}/>

            <Dialog
                fullWidth
                maxWidth="sm"
                open={openInviteModal}
                onClose={() => { if (!inviteSubmitting) closeInviteDialog(); }}
            >
                <DialogTitle>Invitar usuario</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ pt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                autoFocus
                                required
                                label="Correo electrónico"
                                value={inviteForm.email}
                                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Nombre"
                                value={inviteForm.first_name}
                                onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Apellido"
                                value={inviteForm.last_name}
                                onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                loading={inviteOptionsLoading}
                                options={inviteProfileOptions}
                                getOptionLabel={(option) => option?.human_name || option?.name || ''}
                                value={selectedInviteProfiles}
                                onChange={(event, values) => {
                                    setInviteForm({
                                        ...inviteForm,
                                        profile_names: values.map((value) => value.name),
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Perfiles"
                                        helperText="Si no seleccionas perfiles, se asignará Reader por defecto."
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button disabled={inviteSubmitting} onClick={closeInviteDialog}>Cancelar</Button>
                    <LoadingButton
                        loading={inviteSubmitting}
                        variant="contained"
                        onClick={submitInvitation}
                        disabled={inviteSubmitting || !inviteForm.email?.trim()}
                    >
                        Enviar invitación
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default UsersIndex;
